import styled, { css } from 'styled-components';
import { palette } from 'styled-tools';

import RedirectStepIcon from 'icons/redirect-step.svg';
import TruemedLogomark from 'icons/truemed-logomark.svg';

import {
	ACCORDION_FOCUS_RING,
	ACCORDION_ITEM,
	ACCORDION_ITEM_LABEL,
	ACCORDION_SECONDARY_TEXT_COLOR,
} from '../StripeElementsProvider/utils';

const COLLAPSE_DURATION = '0.35s';
const STATE_DURATION = '0.15s';
const FIELD_HEIGHT = '66.4px';
const FIELD_SHADOW = '0 1px 1px rgba(0, 0, 0, 0.03), 0 3px 6px rgba(0, 0, 0, 0.02)';
const FIELD_TEXT_COLOR = '#717171';

const stateTransition = css`
	@media (prefers-reduced-motion: no-preference) {
		transition:
			background-color ${STATE_DURATION} ease,
			border-color ${STATE_DURATION} ease,
			border-radius ${STATE_DURATION} ease,
			box-shadow ${STATE_DURATION} ease,
			color ${STATE_DURATION} ease;
	}
`;

export const Item = styled.div`
	background-color: ${ACCORDION_ITEM.backgroundColor};
	border: ${ACCORDION_ITEM.border};
	border-radius: ${ACCORDION_ITEM.borderRadius};
	box-shadow: ${ACCORDION_ITEM.boxShadow};
	display: flex;
	flex-direction: column;
	width: 100%;
	${stateTransition}
`;

export const RowButton = styled.button<{ $isOpen: boolean }>`
	align-items: center;
	background: none;
	border: 1px solid transparent;
	// The item's own 1px border already insets the content, so side borders here would push the
	// icon 1px right of Stripe's items.
	border-left-width: 0;
	border-right-width: 0;
	border-radius: ${p =>
		p.$isOpen
			? `${ACCORDION_ITEM.borderRadius} ${ACCORDION_ITEM.borderRadius} 0 0`
			: ACCORDION_ITEM.borderRadius};
	color: ${p => (p.$isOpen ? ACCORDION_ITEM_LABEL.selectedColor : ACCORDION_ITEM_LABEL.color)};
	cursor: pointer;
	display: flex;
	font-family: inherit;
	font-size: ${ACCORDION_ITEM_LABEL.fontSize};
	font-weight: ${ACCORDION_ITEM_LABEL.fontWeight};
	line-height: 18.4px;
	// With the item's 1px top and bottom border this makes the collapsed row 55px like Stripe's.
	min-height: 53px;
	padding: ${ACCORDION_ITEM.padding};
	text-align: left;
	width: 100%;
	${stateTransition}

	&:focus-visible {
		outline: 2px solid ${palette('inputBorderFocus')};
		outline-offset: -2px;
	}
`;

// Same slot Stripe gives its method icons, so the label lines up with "Card" and "Bank".
export const IconSlot = styled.span`
	align-items: center;
	display: flex;
	flex-shrink: 0;
	justify-content: flex-start;
	margin-right: 10.5px;
	width: 31.5px;
`;

// Truemed's brand guide requires the logomark in its own teal, so it keeps its own fill instead
// of inheriting the row's label color.
export const OptionLogomark = styled(TruemedLogomark)`
	height: 16.8px;
	width: 16.8px;
`;

export const RowDetail = styled.span`
	color: ${ACCORDION_ITEM_LABEL.color};
	font-weight: 400;
	margin-left: auto;
`;

// Collapses by animating the row from 0fr to 1fr instead of measuring the content. The closed
// panel is visibility:hidden (after the collapse finishes) so its fields leave the tab order.
export const PanelGrid = styled.div<{ $isOpen: boolean }>`
	display: grid;
	grid-template-rows: ${p => (p.$isOpen ? '1fr' : '0fr')};
	visibility: ${p => (p.$isOpen ? 'visible' : 'hidden')};

	@media (prefers-reduced-motion: no-preference) {
		transition:
			grid-template-rows ${COLLAPSE_DURATION} ease,
			visibility 0s linear ${p => (p.$isOpen ? '0s' : COLLAPSE_DURATION)};
	}
`;

export const PanelClip = styled.div`
	min-height: 0;
	overflow: hidden;
`;

export const Panel = styled.div`
	padding: 4px 16px 16px;
`;

export const Fields = styled.div`
	display: grid;
	gap: 12px;
	grid-template-columns: 1fr 1fr;
`;

// Floating-label field matching Stripe's `.Input`: the label rests inside the field and floats up
// on focus or once there is a value.
export const Field = styled.div<{ $isFloated: boolean; $isError?: boolean }>`
	background-color: ${palette('white')};
	border: 2px solid ${p => (p.$isError ? palette('errorRed')(p) : palette('gray2')(p))};
	border-radius: 6px;
	box-shadow: ${FIELD_SHADOW};
	box-sizing: border-box;
	height: ${FIELD_HEIGHT};
	position: relative;

	@media (prefers-reduced-motion: no-preference) {
		transition:
			border-color ${STATE_DURATION} ease,
			box-shadow ${STATE_DURATION} ease;
	}

	&:focus-within {
		border-color: ${palette('buttonBlue')};
		box-shadow: ${FIELD_SHADOW}, ${ACCORDION_FOCUS_RING};
	}

	label {
		opacity: ${p => (p.$isFloated ? 0.8 : 1)};
		transform: ${p => (p.$isFloated ? 'translateY(12px) scale(0.889)' : 'translateY(32px)')};
	}

	&:focus-within label {
		opacity: 0.8;
		transform: translateY(12px) scale(0.889);
	}
`;

export const FieldLabel = styled.label`
	color: ${FIELD_TEXT_COLOR};
	font-size: 16px;
	font-weight: 500;
	left: 12px;
	line-height: 18.4px;
	pointer-events: none;
	position: absolute;
	top: 0;
	transform-origin: left top;

	@media (prefers-reduced-motion: no-preference) {
		transition:
			transform 0.5s cubic-bezier(0.19, 1, 0.22, 1),
			opacity 0.5s cubic-bezier(0.19, 1, 0.22, 1);
	}
`;

const fieldControl = css`
	background: transparent;
	border: 0;
	border-radius: 0;
	box-sizing: border-box;
	color: ${FIELD_TEXT_COLOR};
	font-family: inherit;
	font-size: 16px;
	font-weight: 500;
	height: 100%;
	line-height: 18.4px;
	width: 100%;

	&:focus {
		outline: none;
	}
`;

export const FieldInput = styled.input`
	${fieldControl}
	padding: 32px 12px 12px;
`;

export const FieldSelect = styled.select`
	${fieldControl}
	appearance: none;
	padding: 20px 28px 0 12px;
`;

export const SelectChevron = styled.svg`
	color: ${FIELD_TEXT_COLOR};
	height: 12.8px;
	pointer-events: none;
	position: absolute;
	right: 14px;
	top: calc(50% - 6.4px);
	width: 12.8px;
`;

export const FieldError = styled.p`
	color: ${palette('errorRed')};
	font-size: 14px;
	margin: 8px 0 0;
`;

export const RedirectNotice = styled.div`
	align-items: center;
	color: ${ACCORDION_SECONDARY_TEXT_COLOR};
	display: flex;
	margin-top: 12px;
`;

export const RedirectIcon = styled(RedirectStepIcon)`
	flex-shrink: 0;
	height: 42px;
	margin-right: 12px;
	width: 42px;
`;

export const NoticeText = styled.p`
	color: ${ACCORDION_SECONDARY_TEXT_COLOR};
	font-size: 14px;
	font-weight: 400;
	line-height: 1.45;
	margin: 0;
`;

export const FinePrint = styled.p`
	color: ${ACCORDION_SECONDARY_TEXT_COLOR};
	font-size: 14.88px;
	font-weight: 400;
	line-height: 1.45;
	margin: 8px 0 0;
`;

export const Alert = styled.div`
	color: ${palette('errorRed')};
	font-size: 14px;
	margin-top: 8px;
`;
